import gql from 'graphql-tag';
import { useCallback, useMemo, useState } from 'react';
import { useClient, useQuery } from 'urql';

import { logger } from '@/utils';
import {
  PlayersByIdsQuery,
  PlayersByIdsQueryVariables,
} from './__generated__/usePlayersByIds';

const PLAYERS_BY_IDS_QUERY = gql`
  query PlayersByIds($playerIds: [String!]!) {
    viewer {
      id
      playersSearchV2(playerIds: $playerIds, first: 5000) {
        edges {
          node {
            uuid
            firstName
            lastName
          }
        }
      }
    }
  }
`;

type Player = {
  uuid: string;
  firstName: string;
  lastName: string;
};

export type PlayersBatchProcessing = {
  isProcessing: boolean;
  progress: number;
  totalItems: number;
  processedItems: number;
  errors: string[];
  results: unknown[];
};

type BatchProcessingState = PlayersBatchProcessing & {
  totalBatches: number;
  processedBatches: number;
};

type UsePlayersByIdsOptions = {
  enableBatchProcessing?: boolean;
  batchSize?: number;
};

// Default batch size for processing player IDs
const BATCH_SIZE = 1000;

export function usePlayersByIds(options: UsePlayersByIdsOptions = {}) {
  const { enableBatchProcessing = false, batchSize = BATCH_SIZE } = options;
  const client = useClient();
  const [playerIds, setPlayerIds] = useState<string[]>([]);

  const [batchState, setBatchState] = useState<BatchProcessingState>({
    isProcessing: false,
    progress: 0,
    totalBatches: 0,
    processedBatches: 0,
    totalItems: 0,
    processedItems: 0,
    results: [],
    errors: [],
  });

  const [{ data, fetching }, refresh] = useQuery<
    PlayersByIdsQuery,
    PlayersByIdsQueryVariables
  >({
    query: PLAYERS_BY_IDS_QUERY,
    pause:
      !playerIds.length ||
      (enableBatchProcessing && playerIds.length > batchSize),
    variables: {
      playerIds,
    },
  });

  const processBatch = useCallback(
    async (batchPlayerIds: string[]): Promise<Player[]> => {
      try {
        const result = await client
          .query(PLAYERS_BY_IDS_QUERY, { playerIds: batchPlayerIds })
          .toPromise();

        if (result.error) {
          throw new Error(result.error.message);
        }

        return (
          result.data?.viewer.playersSearchV2?.edges?.map(
            (edge: any) => edge?.node,
          ) || []
        );
      } catch (error) {
        throw new Error(
          error instanceof Error ? error.message : 'Unknown error occurred',
        );
      }
    },
    [client],
  );

  const processPlayerIds = useCallback(
    async (allPlayerIds: string[]): Promise<Player[]> => {
      if (allPlayerIds.length === 0) {
        return [];
      }

      const totalItems = allPlayerIds.length;

      const batches = Array.from(
        { length: Math.ceil(allPlayerIds.length / batchSize) },
        (_, index) =>
          allPlayerIds.slice(index * batchSize, (index + 1) * batchSize),
      );

      const totalBatches = batches.length;

      setBatchState({
        isProcessing: true,
        progress: 0,
        totalBatches,
        processedBatches: 0,
        totalItems,
        processedItems: 0,
        results: [],
        errors: [],
      });

      try {
        // Process batches sequentially using a functional approach
        const processBatchWithIndex = async (
          batch: string[],
          index: number,
        ) => {
          const batchNumber = index + 1;

          try {
            const batchResults = await processBatch(batch);
            const processedBatches = batchNumber;

            const processedItems = Math.min(
              processedBatches * batchSize,
              totalItems,
            );

            const progress = (processedItems / totalItems) * 100;

            setBatchState((prev) => ({
              ...prev,
              processedBatches,
              processedItems,
              progress,
              results: [...prev.results, ...batchResults],
              errors: prev.errors,
            }));

            // Add a small delay to show progress (optional)
            if (index < batches.length - 1) {
              await new Promise((resolve) => setTimeout(resolve, 100));
            }

            return { success: true, results: batchResults, error: null };
          } catch (error) {
            const errorMessage = `Batch ${batchNumber} failed: ${
              error instanceof Error ? error.message : 'Unknown error'
            }`;

            logger.error(`❌ Error processing batch ${batchNumber}:`, error);

            setBatchState((prev) => ({
              ...prev,
              errors: [...prev.errors, errorMessage],
            }));

            return { success: false, results: [], error: errorMessage };
          }
        };

        // Process all batches sequentially
        const batchResults: Player[] = [];
        const batchErrors = [];

        await batches.reduce(async (previousPromise, batch, index) => {
          await previousPromise;
          const result = await processBatchWithIndex(batch, index);

          if (result.success) {
            batchResults.push(...result.results);
          } else {
            batchErrors.push(result.error);
          }
        }, Promise.resolve());

        setBatchState((prev) => ({
          ...prev,
          isProcessing: false,
          progress: 100,
        }));

        return batchResults;
      } catch (error) {
        setBatchState((prev) => ({
          ...prev,
          isProcessing: false,
          errors: [...prev.errors, 'Processing failed'],
        }));
        throw error;
      }
    },
    [processBatch, batchSize],
  );

  const resetBatchState = useCallback(() => {
    setBatchState({
      isProcessing: false,
      progress: 0,
      totalBatches: 0,
      processedBatches: 0,
      totalItems: 0,
      processedItems: 0,
      results: [],
      errors: [],
    });
  }, []);

  const players = useMemo(() => {
    // If we're using batch processing or have batch results, return batch results
    if (enableBatchProcessing && playerIds.length > batchSize) {
      return batchState.results;
    }

    // Otherwise, use the regular query results
    const edges = data?.viewer.playersSearchV2?.edges;

    return edges?.reduce<
      NonNullable<
        NonNullable<
          PlayersByIdsQuery['viewer']['playersSearchV2']['edges']
        >[number]
      >['node'][]
    >((acc, edge) => {
      if (edge?.node) {
        acc = [...acc, edge.node];
      }

      return acc;
    }, []);
  }, [
    data?.viewer.playersSearchV2.edges,
    batchState.results,
    playerIds.length,
    batchSize,
    enableBatchProcessing,
  ]);

  const isLoading = useMemo(() => {
    return enableBatchProcessing && playerIds.length > batchSize
      ? batchState.isProcessing
      : fetching;
  }, [
    playerIds.length,
    batchState.isProcessing,
    fetching,
    enableBatchProcessing,
    batchSize,
  ]);

  const setPlayerIdsWithBatching = useCallback(
    (newPlayerIds: string[]) => {
      resetBatchState();

      if (enableBatchProcessing && newPlayerIds.length > batchSize) {
        processPlayerIds(newPlayerIds);
        return;
      }

      setPlayerIds(newPlayerIds);
    },
    [processPlayerIds, resetBatchState, enableBatchProcessing, batchSize],
  );

  return {
    refresh,
    fetching: isLoading,
    players,
    setPlayerIds: setPlayerIdsWithBatching,
    batchProcessing: {
      ...batchState,
      processPlayerIds,
      resetState: resetBatchState,
    },
  };
}
